import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardHeader, CardContent, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ContractForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const isEditing = !!id;

  const [formData, setFormData] = useState({
    name: '',
    supplier_id: '',
    location: '',
    start_date: '',
    end_date: '',
    amount: '',
    type_contract: '',
    assigned_user_id: '',
    description: '',
    reminder_days: 30
  });

  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isEditing);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [newSupplier, setNewSupplier] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    description: ''
  });

  const contractTypes = [
    'Ligne téléphonique',
    'Fibre optique', 
    'Hébergement web',
    'Logiciel SaaS',
    'Maintenance informatique',
    'Sécurité informatique',
    'Cloud computing',
    'Autres'
  ];

  useEffect(() => {
    fetchUsers();
    fetchSuppliers();
    if (isEditing) {
      fetchContract();
    }
  }, [id]);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await axios.get(`${API}/suppliers`);
      setSuppliers(response.data);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      toast.error('Erreur lors du chargement des fournisseurs');
    }
  };

  const fetchContract = async () => {
    try {
      const response = await axios.get(`${API}/contracts/${id}`);
      const contract = response.data;
      
      // Format dates for input fields
      setFormData({
        name: contract.name,
        supplier_id: contract.supplier_id || '',
        location: contract.location || '',
        start_date: new Date(contract.start_date).toISOString().split('T')[0],
        end_date: new Date(contract.end_date).toISOString().split('T')[0],
        amount: contract.amount,
        type_contract: contract.type_contract,
        assigned_user_id: contract.assigned_user_id || '',
        description: contract.description || '',
        reminder_days: contract.reminder_days || 30
      });
    } catch (error) {
      console.error('Error fetching contract:', error);
      toast.error('Erreur lors du chargement du contrat');
      navigate('/contracts');
    } finally {
      setInitialLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateSupplier = async (e) => {
    e.preventDefault();
    
    if (!newSupplier.name.trim()) {
      toast.error('Le nom du fournisseur est requis');
      return;
    }

    try {
      const response = await axios.post(`${API}/suppliers`, newSupplier);
      toast.success('Fournisseur créé avec succès');
      
      // Refresh suppliers list and select the new one
      await fetchSuppliers();
      setFormData(prev => ({ ...prev, supplier_id: response.data.id }));
      
      // Reset and close modal
      setNewSupplier({
        name: '',
        contact_person: '',
        email: '',
        phone: '',
        address: '',
        description: ''
      });
      setShowSupplierModal(false);
    } catch (error) {
      console.error('Error creating supplier:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du fournisseur');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Validate required fields
      if (!formData.name || !formData.supplier_id || !formData.start_date || !formData.end_date || !formData.amount || !formData.type_contract) {
        toast.error('Veuillez remplir tous les champs obligatoires');
        setLoading(false);
        return;
      }

      // Validate dates
      if (new Date(formData.end_date) <= new Date(formData.start_date)) {
        toast.error('La date de fin doit être postérieure à la date de début');
        setLoading(false);
        return;
      }

      // Validate amount
      if (parseFloat(formData.amount) <= 0) {
        toast.error('Le montant doit être positif');
        setLoading(false);
        return;
      }

      // Prepare data for API
      const submitData = {
        name: formData.name,
        supplier_id: formData.supplier_id,
        location: formData.location || null,
        start_date: new Date(formData.start_date).toISOString(),
        end_date: new Date(formData.end_date).toISOString(),
        amount: parseFloat(formData.amount),
        type_contract: formData.type_contract,
        assigned_user_id: formData.assigned_user_id || null,
        description: formData.description || null,
        reminder_days: parseInt(formData.reminder_days)
      };

      if (isEditing) {
        await axios.put(`${API}/contracts/${id}`, submitData);
        toast.success('Contrat mis à jour avec succès');
      } else {
        await axios.post(`${API}/contracts`, submitData);
        toast.success('Contrat créé avec succès');
      }

      navigate('/contracts');
    } catch (error) {
      console.error('Error saving contract:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="contract-form-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary">
            {isEditing ? 'Modifier le contrat' : 'Nouveau contrat'}
          </h1>
          <p className="text-text-muted mt-1">
            {isEditing ? 'Modifiez les informations du contrat' : 'Créez un nouveau contrat'}
          </p>
        </div>
        <Button
          variant="outline"
          onClick={() => navigate('/contracts')}
          data-testid="back-to-contracts-button"
          className="btn-outline"
        >
          <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Retour
        </Button>
      </div>

      {/* Form */}
      <Card>
        <CardHeader>
          <CardTitle>Informations du contrat</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="form-label required">Nom du contrat *</Label>
                <Input
                  id="name"
                  name="name"
                  placeholder="Ex: Contrat fibre optique bureau principal"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  data-testid="contract-name-input"
                  className="form-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="location" className="form-label">Localisation</Label>
                <Input
                  id="location"
                  name="location"
                  placeholder="Ex: Siège Paris, Bureau Lyon..."
                  value={formData.location}
                  onChange={handleInputChange}
                  data-testid="contract-location-input"
                  className="form-input"
                />
              </div>
            </div>

            {/* Supplier with Quick Add */}
            <div className="space-y-2">
              <Label htmlFor="supplier_id" className="form-label required">Fournisseur *</Label>
              <div className="flex gap-2">
                <select
                  id="supplier_id"
                  name="supplier_id"
                  value={formData.supplier_id}
                  onChange={handleInputChange}
                  required
                  data-testid="contract-supplier-select"
                  className="form-select flex-1"
                >
                  <option value="">Sélectionnez un fournisseur</option>
                  {suppliers.map(supplier => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
                <Button
                  type="button"
                  onClick={() => setShowSupplierModal(true)}
                  className="btn-primary"
                  title="Créer un nouveau fournisseur"
                  data-testid="add-supplier-quick-button"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                </Button>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="start_date" className="form-label required">Date de début *</Label>
                <Input
                  id="start_date"
                  name="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                  required
                  data-testid="contract-start-date-input"
                  className="form-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="end_date" className="form-label required">Date de fin *</Label>
                <Input
                  id="end_date"
                  name="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                  required
                  data-testid="contract-end-date-input"
                  className="form-input"
                />
              </div>
            </div>

            {/* Amount and Type */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="amount" className="form-label required">Montant (€) *</Label>
                <Input
                  id="amount"
                  name="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="Ex: 1500.00"
                  value={formData.amount}
                  onChange={handleInputChange}
                  required
                  data-testid="contract-amount-input"
                  className="form-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="type_contract" className="form-label required">Type de contrat *</Label>
                <select
                  id="type_contract"
                  name="type_contract"
                  value={formData.type_contract}
                  onChange={handleInputChange}
                  required
                  data-testid="contract-type-select"
                  className="form-select"
                >
                  <option value="">Sélectionnez un type</option>
                  {contractTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Assignment and Reminder */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="assigned_user_id" className="form-label">Assigné à</Label>
                <select
                  id="assigned_user_id"
                  name="assigned_user_id"
                  value={formData.assigned_user_id}
                  onChange={handleInputChange}
                  data-testid="contract-assigned-user-select"
                  className="form-select"
                >
                  <option value="">Non assigné</option>
                  {users.map(user => (
                    <option key={user.id} value={user.id}>
                      {user.full_name} ({user.email})
                    </option>
                  ))}
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reminder_days" className="form-label">Rappel (jours avant échéance)</Label>
                <Input
                  id="reminder_days"
                  name="reminder_days"
                  type="number"
                  min="1"
                  max="365"
                  value={formData.reminder_days}
                  onChange={handleInputChange}
                  data-testid="contract-reminder-days-input"
                  className="form-input"
                />
              </div>
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="form-label">Description</Label>
              <Textarea
                id="description"
                name="description"
                placeholder="Détails supplémentaires sur le contrat..."
                rows={4}
                value={formData.description}
                onChange={handleInputChange}
                data-testid="contract-description-input"
                className="form-textarea"
              />
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-border-light">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/contracts')}
                data-testid="cancel-form-button"
                className="btn-outline"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={loading}
                data-testid="submit-form-button"
                className="btn-primary"
              >
                {loading ? (
                  <>
                    <div className="loading-spinner mr-2"></div>
                    {isEditing ? 'Modification...' : 'Création...'}
                  </>
                ) : (
                  isEditing ? 'Modifier le contrat' : 'Créer le contrat'
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Quick Add Supplier Modal */}
      {showSupplierModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={() => setShowSupplierModal(false)}>
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-border-light">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold text-text-primary">Créer un fournisseur</h3>
                <button
                  onClick={() => setShowSupplierModal(false)}
                  className="text-text-muted hover:text-text-primary"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            <form onSubmit={handleCreateSupplier} className="p-6 space-y-4">
              <div>
                <Label htmlFor="new-supplier-name" className="form-label required">Nom du fournisseur *</Label>
                <Input
                  id="new-supplier-name"
                  type="text"
                  placeholder="Orange Business Services"
                  value={newSupplier.name}
                  onChange={(e) => setNewSupplier({...newSupplier, name: e.target.value})}
                  required
                  className="form-input"
                />
              </div>

              <div>
                <Label htmlFor="new-supplier-contact" className="form-label">Personne de contact</Label>
                <Input
                  id="new-supplier-contact"
                  type="text"
                  placeholder="Jean Dupont"
                  value={newSupplier.contact_person}
                  onChange={(e) => setNewSupplier({...newSupplier, contact_person: e.target.value})}
                  className="form-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="new-supplier-email" className="form-label">Email</Label>
                  <Input
                    id="new-supplier-email"
                    type="email"
                    placeholder="contact@email.com"
                    value={newSupplier.email}
                    onChange={(e) => setNewSupplier({...newSupplier, email: e.target.value})}
                    className="form-input"
                  />
                </div>

                <div>
                  <Label htmlFor="new-supplier-phone" className="form-label">Téléphone</Label>
                  <Input
                    id="new-supplier-phone"
                    type="tel"
                    placeholder="01 23 45 67 89"
                    value={newSupplier.phone}
                    onChange={(e) => setNewSupplier({...newSupplier, phone: e.target.value})}
                    className="form-input"
                  />
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowSupplierModal(false)}
                  className="btn-outline"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="btn-primary"
                >
                  Créer
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
